/**
 * Variety Pack Nutrition Panel - Tab Switching Logic
 * Ticket: ALO-74
 *
 * Handles:
 * - Pill/tab selection
 * - Panel visibility switching
 * - Keyboard navigation (arrow keys)
 * - URL hash linking (for deep linking from product offer section)
 */

(function () {
  'use strict';

  const SELECTORS = {
    section: '.acq-variety-nutrition',
    pill: '.acq-variety-nutrition__pill',
    panel: '.acq-variety-nutrition__panel',
    pillsContainer: '.acq-variety-nutrition__pills',
  };

  const CLASSES = {
    active: 'is-active',
  };

  /**
   * Initialize all variety nutrition sections on the page
   */
  function init() {
    const sections = document.querySelectorAll(SELECTORS.section);
    sections.forEach(initSection);

    // Handle hash changes for deep linking
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Check on initial load
  }

  /**
   * Initialize a single section
   * @param {HTMLElement} section
   */
  function initSection(section) {
    const pills = section.querySelectorAll(SELECTORS.pill);
    const panels = section.querySelectorAll(SELECTORS.panel);
    const pillsContainer = section.querySelector(SELECTORS.pillsContainer);

    if (!pills.length || !panels.length) return;

    // Click handlers for pills
    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const index = pill.dataset.flavorIndex;
        activateFlavor(section, index);
      });
    });

    // Keyboard navigation
    if (pillsContainer) {
      pillsContainer.addEventListener('keydown', (e) => {
        handleKeyboardNav(e, pills);
      });
    }
  }

  /**
   * Activate a specific flavor by index
   * @param {HTMLElement} section
   * @param {string|number} index
   */
  function activateFlavor(section, index) {
    const pills = section.querySelectorAll(SELECTORS.pill);
    const panels = section.querySelectorAll(SELECTORS.panel);

    // Update pills
    pills.forEach((pill) => {
      const isActive = pill.dataset.flavorIndex === String(index);
      pill.classList.toggle(CLASSES.active, isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      pill.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panels
    panels.forEach((panel) => {
      const isActive = panel.dataset.flavorIndex === String(index);
      panel.classList.toggle(CLASSES.active, isActive);
      panel.hidden = !isActive;
    });

    // Scroll active pill into view on mobile
    const activePill = section.querySelector(`${SELECTORS.pill}.${CLASSES.active}`);
    if (activePill) {
      activePill.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  /**
   * Handle keyboard navigation within pills
   * @param {KeyboardEvent} e
   * @param {NodeList} pills
   */
  function handleKeyboardNav(e, pills) {
    const currentIndex = Array.from(pills).findIndex(
      (pill) => pill === document.activeElement
    );

    if (currentIndex === -1) return;

    let nextIndex;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = (currentIndex + 1) % pills.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = (currentIndex - 1 + pills.length) % pills.length;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = pills.length - 1;
        break;
      default:
        return;
    }

    pills[nextIndex].focus();
    pills[nextIndex].click();
  }

  /**
   * Handle URL hash changes for deep linking
   * Allows linking directly to a flavor: #variety-nutrition-flavor-2
   */
  function handleHashChange() {
    const hash = window.location.hash;
    if (!hash) return;

    // Match pattern: #variety-nutrition-flavor-{index} or #variety-nutrition-{sectionId}-flavor-{index}
    const match = hash.match(/variety-nutrition(?:-([^-]+))?-flavor-(\d+)/);
    if (!match) return;

    const [, sectionId, flavorIndex] = match;

    let section;
    if (sectionId) {
      section = document.querySelector(`#variety-nutrition-${sectionId}`);
    } else {
      section = document.querySelector(SELECTORS.section);
    }

    if (section) {
      activateFlavor(section, flavorIndex);

      // Scroll section into view
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  /**
   * Public API for external scripts to activate a flavor
   * Usage: window.VarietyNutrition.activateFlavor(sectionId, flavorIndex)
   */
  window.VarietyNutrition = {
    activateFlavor: function (sectionId, flavorIndex) {
      const section = document.querySelector(`#variety-nutrition-${sectionId}`);
      if (section) {
        activateFlavor(section, flavorIndex);
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
